import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyTierThresholds, { getTierThreshold } from 'loyalty/selectors/getLoyaltyTierThresholds'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import Clickable from 'components/Common/Clickable'

const CardContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: ${rem(8)};
  overflow: hidden;
  box-shadow:
    0 ${rem(1)} ${rem(1.5)} rgba(0, 0, 0, 0.07),
    0 ${rem(1.5)} ${rem(5)} rgba(0, 0, 0, 0.05);
`

const CardHeader = styled.div`
  padding: ${rem(10)} ${rem(12)};
  ${tierGradientBackgroundStyle}
`

const HeaderText = styled.span`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(14)};
  font-weight: ${props => props.theme.font.primary.weight.bold};
  line-height: ${rem(18)};
  ${tierNormalContrastColorStyle}
`

const CardBody = styled.div`
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  padding: ${rem(16)};
`

const HeaderToggle = styled.div`
  display: inline-flex;
  align-items: stretch;
  background: rgba(0, 0, 0, 0.15);
  border-radius: ${rem(5)};
  padding: ${rem(2)};
  gap: ${rem(2)};
`

const HeaderToggleOption = styled(Clickable)`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(12)};
  line-height: ${rem(16)};
  padding: ${rem(4)} ${rem(8)};
  border-radius: ${rem(3)};
  transition: background 150ms, opacity 150ms;
  ${tierNormalContrastColorStyle}

  &.is-active {
    font-weight: ${props => props.theme.font.primary.weight.bold};
    background: rgba(255, 255, 255, 0.3);
    opacity: 1;
  }

  &.is-inactive {
    font-weight: ${props => props.theme.font.primary.weight.regular};
    opacity: 0.7;
  }
`

const BodyTextStyled = styled.p`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(12)};
  font-weight: ${props => props.theme.font.primary.weight.regular};
  line-height: ${rem(16)};
  /* Using bronze contrast instead of neural one to get the same color as the figma design */
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  margin: 0;
`

const BoldText = styled.span`
  font-weight: ${props => props.theme.font.primary.weight.superBold};
`

interface Props {
  /**
   * The tier to display (next tier for unlock, current tier for retain)
   */
  tier: App.LoyaltyTier
  /**
   * Whether this is for retaining current tier (true) or unlocking next tier (false)
   */
  isRetain?: boolean
  creditsRemaining: number
  nightsRemaining: number
  /**
   * The end of the member's current membership year. Used as the "by" date in unlock mode.
   */
  membershipYearEndDate?: string
  /**
   * The date the member's tier is reviewed. Used as the "by" date in retain mode.
   */
  tierReviewDate?: string
  showToggle?: boolean
  showUpgradeView?: boolean
  onToggleView?: (showUpgrade: boolean) => void
  currentTierName?: string
  nextTierName?: string
  /**
   * For retain mode only: which membership year contains the tier review.
   * - `'current-year'` (default): tier review at end of current membership year. Show calculated remaining (bolded).
   * - `'next-year'`: member upgraded mid-year and the tier review is in the *next* membership year. Show absolute SC/Nights requirements (not bolded).
   */
  retainVariant?: 'current-year' | 'next-year'
}

function LoyaltyDrawerTierCard({
  tier,
  isRetain = false,
  creditsRemaining,
  nightsRemaining,
  membershipYearEndDate,
  tierReviewDate,
  showToggle = false,
  showUpgradeView = false,
  onToggleView,
  currentTierName,
  nextTierName,
  retainVariant = 'current-year',
}: Props) {
  const intl = useIntl()
  const tierThresholds = useAppSelector(getLoyaltyTierThresholds)
  const tierName = getLoyaltyTierDisplayName(tier)

  const hasCredits = creditsRemaining > 0
  const hasNights = nightsRemaining > 0

  const toRetainText = intl.formatMessage(
    {
      id: 'zvrL6f2WsFsW2a8dOo7t',
      defaultMessage: 'To retain {tierName}',
      description: 'Header text for tier retention requirement in the loyalty section',
    },
    { tierName },
  )
  const toUnlockText = intl.formatMessage(
    {
      id: 'LPYDBrvhK439rFvswhrv',
      defaultMessage: 'To unlock {tierName}',
      description: 'Header text for tier unlock requirement in the loyalty program section',
    },
    { tierName },
  )
  const headerText = isRetain ? toRetainText : toUnlockText

  const expiryDate = isRetain ? tierReviewDate : membershipYearEndDate
  const formattedExpiryDate = expiryDate ? <FormatDateTime value={expiryDate} format="dmy-casual-short" /> : undefined
  const boldTag = (chunks: React.ReactNode) => <BoldText>{chunks}</BoldText>

  let bodyText: React.ReactNode
  if (isRetain && retainVariant === 'next-year') {
    const absoluteThreshold = tierThresholds ? getTierThreshold(tierThresholds, tier) : undefined
    const absoluteCredits = absoluteThreshold?.statusCredits ?? 0
    const absoluteNights = absoluteThreshold?.nights ?? 0
    if (expiryDate) {
      bodyText = <FormattedMessage
        id="bbSjG5vsUl4ydIKca7oV"
        defaultMessage="Within your next membership year, you will need {creditsCount, plural, one {# Status Credit} other {# Status Credits}} and {nightsCount, plural, one {# Night} other {# Nights}} by {expiryDate}."
        description="Drawer card retain next-year variant: absolute requirements with tier review date for upgraded members whose review falls in the following membership year"
        values={{ creditsCount: absoluteCredits, nightsCount: absoluteNights, expiryDate: formattedExpiryDate }}
      />
    } else {
      bodyText = <FormattedMessage
        id="LXXHS3_hgCkVx8-2fWUL"
        defaultMessage="Within your next membership year, you will need {creditsCount, plural, one {# Status Credit} other {# Status Credits}} and {nightsCount, plural, one {# Night} other {# Nights}}."
        description="Drawer card retain next-year variant: absolute requirements without expiry for upgraded members"
        values={{ creditsCount: absoluteCredits, nightsCount: absoluteNights }}
      />
    }
  } else if (hasCredits && hasNights && expiryDate) {
    bodyText = <FormattedMessage
      id="HbnvaQmTGprfYgxmGYnS"
      defaultMessage="You need <bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold> and <bold>{nightsCount, plural, one {# night} other {# nights}}</bold> by {expiryDate}."
      description="Drawer card: credits and nights needed with expiry date"
      values={{ creditsCount: creditsRemaining, nightsCount: nightsRemaining, expiryDate: formattedExpiryDate, bold: boldTag }}
    />
  } else if (hasCredits && hasNights) {
    bodyText = <FormattedMessage
      id="BksWpQ-lkV8lLNCvBXJI"
      defaultMessage="You need <bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold> and <bold>{nightsCount, plural, one {# night} other {# nights}}</bold>."
      description="Drawer card: credits and nights needed, no expiry in the loyalty program section"
      values={{ creditsCount: creditsRemaining, nightsCount: nightsRemaining, bold: boldTag }}
    />
  } else if (hasCredits && expiryDate) {
    bodyText = <FormattedMessage
      id="-F7esi0lfIcxuwcZqIKj"
      defaultMessage="You need <bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold> by {expiryDate}."
      description="Drawer card: credits needed with expiry date in the loyalty program section"
      values={{ creditsCount: creditsRemaining, expiryDate: formattedExpiryDate, bold: boldTag }}
    />
  } else if (hasCredits) {
    bodyText = <FormattedMessage
      id="GVNhIi94GzO8temKvHWX"
      defaultMessage="You need <bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold>."
      description="Drawer card: credits needed, no expiry in the loyalty program section"
      values={{ creditsCount: creditsRemaining, bold: boldTag }}
    />
  } else if (expiryDate) {
    bodyText = <FormattedMessage
      id="BRIAaL7ikGNRklDOVldH"
      defaultMessage="You need <bold>{nightsCount, plural, one {# night} other {# nights}}</bold> by {expiryDate}."
      description="Drawer card: nights needed with expiry date in the loyalty program section"
      values={{ nightsCount: nightsRemaining, expiryDate: formattedExpiryDate, bold: boldTag }}
    />
  } else {
    bodyText = <FormattedMessage
      id="iAlsAllYSdzDk9L5woEp"
      defaultMessage="You need <bold>{nightsCount, plural, one {# night} other {# nights}}</bold>."
      description="Drawer card: nights needed, no expiry in the loyalty program section"
      values={{ nightsCount: nightsRemaining, bold: boldTag }}
    />
  }

  return (
    <CardContainer>
      <CardHeader className={`tier-${tier}`}>
        {showToggle && currentTierName && nextTierName && (
          <HeaderToggle>
            <HeaderToggleOption
              className={`tier-${tier} ${!showUpgradeView ? 'is-active' : 'is-inactive'}`}
              onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onToggleView?.(false) }}
            >
              {intl.formatMessage(
                { id: '9ZN5FHO5pQctKoGZ26Aw', defaultMessage: 'Retain {tierName}', description: 'Toggle option to view current tier retention requirements' },
                { tierName: currentTierName },
              )}
            </HeaderToggleOption>
            <HeaderToggleOption
              className={`tier-${tier} ${showUpgradeView ? 'is-active' : 'is-inactive'}`}
              onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onToggleView?.(true) }}
            >
              {intl.formatMessage(
                { id: 'NilqMdlNHrDTNigNu1IR', defaultMessage: 'Unlock {tierName}', description: 'Toggle option to view next tier upgrade requirements' },
                { tierName: nextTierName },
              )}
            </HeaderToggleOption>
          </HeaderToggle>
        )}
        {!(showToggle && currentTierName && nextTierName) && (
          <HeaderText className={`tier-${tier}`}>
            {headerText}
          </HeaderText>
        )}
      </CardHeader>
      <CardBody>
        <BodyTextStyled>
          {bodyText}
        </BodyTextStyled>
      </CardBody>
    </CardContainer>
  )
}

export default LoyaltyDrawerTierCard
